import { and, ControlProps, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsControlProps } from '@jsonforms/react'

import { authSelectors } from 'ducks/auth'
import { Chip, Typography, CircularProgress, TextField } from 'opensolar-ui'
import { useCallback, useEffect, useState } from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import restClient from 'restClient'
import { useDebouncedCallback } from 'util/Debounce'
import {
  ChipsContainer,
  Container,
  Description,
  OptionCode,
  OptionItem,
  OptionName,
  StyledAutocomplete,
} from './styles'

const restClientInstance = restClient(window.API_ROOT + '/api')

const tester = rankWith(100, and(uiTypeIs('AutocompleteSearch')))

const AutocompleteSearchRenderer = ({ data, handleChange, path, uischema }: ControlProps) => {
  const translate = useTranslate()
  const orgId = useSelector(authSelectors.getOrgId)

  const rawLabel = uischema?.label
  const label = typeof rawLabel === 'string' ? rawLabel : translate('Search')
  const endpoint = (uischema?.options?.endpoint as string) || 'utility_tariffs'
  const description = uischema?.options?.description as string | undefined
  const optionText = (uischema?.options?.optionText as string) || 'code'
  const optionValue = (uischema?.options?.optionValue as string) || 'code'
  const single = (uischema?.options?.single as boolean) || false
  const orgSpecific = (uischema?.options?.orgSpecific as boolean) !== false // Default to true for backward compatibility

  const extractValue = (value: any): string => {
    if (!value) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'object' && value[optionValue]) return value[optionValue]
    return ''
  }

  const selectedValues: string[] = single
    ? (data ? [extractValue(data)] : [])
    : Array.isArray(data) ? data.map(extractValue).filter(Boolean) : []

  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<Record<string, any>[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedOption, setSelectedOption] = useState<Record<string, any> | null>(null)

  // Load selected value when in single mode and data exists
  useEffect(() => {
    const dataValue = extractValue(data)
    if (single && dataValue) {
      // If data is already an object with the expected structure, use it directly
      if (data && typeof data === 'object' && !Array.isArray(data) && data[optionValue]) {
        setSelectedOption(data as Record<string, any>)
        return
      }
      
      // Otherwise, fetch it by ID
      const loadSelectedValue = async () => {
        if (!orgId) return
        setLoading(true)
        try {
          const baseUrl = orgSpecific
            ? `${window.API_ROOT}/api/orgs/${orgId}/${endpoint}`
            : `${window.API_ROOT}/api/${endpoint}`
          const response = await restClientInstance('CUSTOM_GET', 'custom', {
            url: `${baseUrl}/?search=${encodeURIComponent(dataValue)}&limit=1`,
          })
          if (response.data && response.data.length > 0) {
            const found = response.data.find((opt: Record<string, any>) => opt[optionValue] === dataValue)
            if (found) {
              setSelectedOption(found)
            }
          }
        } catch (error) {
          console.error('Failed to load selected value:', error)
        } finally {
          setLoading(false)
        }
      }
      loadSelectedValue()
    } else if (single && (!data || dataValue === '')) {
      setSelectedOption(null)
    }
  }, [single, data, orgId, endpoint, optionValue, orgSpecific])

  const searchItems = useCallback(
    async (searchText: string) => {
      if (!orgId || searchText.length < 2) {
        setOptions([])
        return
      }

      setLoading(true)
      try {
        const baseUrl = orgSpecific
          ? `${window.API_ROOT}/api/orgs/${orgId}/${endpoint}`
          : `${window.API_ROOT}/api/${endpoint}`
        const response = await restClientInstance('CUSTOM_GET', 'custom', {
          url: `${baseUrl}/?search=${encodeURIComponent(searchText)}&limit=20`,
        })
        setOptions(response.data || [])
      } catch (error) {
        console.error('Failed to search:', error)
        setOptions([])
      } finally {
        setLoading(false)
      }
    },
    [orgId, endpoint, orgSpecific]
  )

  const debouncedSearch = useDebouncedCallback(searchItems, 300)

  const handleInputChange = useCallback(
    (_: any, value: string) => {
      setInputValue(value)
      if (value) {
        debouncedSearch(value)
      } else {
        setOptions([])
      }
    },
    [debouncedSearch]
  )

  const handleAddItem = (item: Record<string, any> | null) => {
    if (!item) {
      if (single) {
        handleChange(path, '')
      }
      return
    }
    const value = item[optionValue]
    if (single) {
      handleChange(path, value)
      setInputValue('')
      setOptions([])
    } else {
      if (selectedValues.includes(value)) return
      handleChange(path, [...selectedValues, value])
      setInputValue('')
      setOptions([])
    }
  }

  const handleRemoveItem = (value: string) => {
    if (single) {
      handleChange(path, '')
      setSelectedOption(null)
    } else {
      handleChange(
        path,
        selectedValues.filter((v) => v !== value)
      )
    }
  }

  return (
    <Container>
      <div>
        <Typography textVariant="body1Bold">{label}</Typography>
        {description && <Description colorHex="#6B6A76">{translate(description)}</Description>}
      </div>

      <StyledAutocomplete
        options={options}
        getOptionLabel={(option) => {
          const opt = option as Record<string, any>
          return `${opt[optionText] || opt[optionValue]}`
        }}
        getOptionSelected={(option, value) => {
          if (!option || !value) return false
          const opt = option as Record<string, any>
          const val = value as Record<string, any>
          return opt[optionValue] === val[optionValue]
        }}
        filterOptions={(x) => x}
        loading={loading}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={(_, value) => {
          handleAddItem(value as Record<string, any> | null)
          setSelectedOption(value as Record<string, any> | null)
        }}
        value={single ? (selectedOption || (selectedValues.length > 0 ? options.find(opt => opt[optionValue] === selectedValues[0]) || null : null)) : null}
        noOptionsText={inputValue.length < 2 ? 'Type to search...' : 'No results found'}
        renderOption={(option) => {
          const opt = option as Record<string, any>
          return (
            <OptionItem>
              <OptionName>{opt.name || opt[optionText]}</OptionName>
              <OptionCode>{opt[optionValue]}</OptionCode>
            </OptionItem>
          )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={translate('Type to search...')}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {selectedValues.length > 0 && (
        <ChipsContainer>
          {selectedValues.map((value) => {
            const displayValue = single && options.length > 0
              ? (options.find(opt => opt[optionValue] === value)?.[optionText] || value)
              : value
            return (
              <Chip key={value} label={displayValue} onDelete={() => handleRemoveItem(value)} size="small" />
            )
          })}
        </ChipsContainer>
      )}
    </Container>
  )
}

const renderer = withJsonFormsControlProps(AutocompleteSearchRenderer)

export default {
  tester,
  renderer,
}
